import React, { useState, useEffect } from 'react';
import { scorecardAPI } from '../lib/api';
import { useNotification } from '../contexts/NotificationContext';
import {
  Shield,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  CheckCircle,
  FileText,
  Loader2,
  Activity,
  Target
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';

const SecurityScorecard = () => {
  const { notify } = useNotification();
  const [scorecard, setScorecard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchScorecard();
  }, []);

  const fetchScorecard = async () => {
    try {
      const res = await scorecardAPI.get();
      setScorecard(res.data);
    } catch (err) {
      notify.error('Failed to load scorecard');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 75) return 'text-green-400';
    if (score >= 50) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getScoreBg = (score) => {
    if (score >= 75) return 'bg-green-500/20 border-green-500/30';
    if (score >= 50) return 'bg-yellow-500/20 border-yellow-500/30';
    return 'bg-red-500/20 border-red-500/30';
  };

  const getTrend = () => {
    if (!scorecard?.trend || scorecard.trend.length < 2) return null;
    const recent = scorecard.trend[scorecard.trend.length - 1]?.overallScore || 0;
    const previous = scorecard.trend[scorecard.trend.length - 2]?.overallScore || 0;
    const diff = recent - previous;
    if (diff > 0) return { icon: TrendingUp, color: 'text-green-400', text: `+${diff.toFixed(1)}` };
    if (diff < 0) return { icon: TrendingDown, color: 'text-red-400', text: diff.toFixed(1) };
    return { icon: Minus, color: 'text-slate-400', text: '0' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-violet-500 animate-spin" />
      </div>
    );
  }

  const trend = getTrend();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pt-16">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Target className="w-7 h-7 text-violet-400" />
            Security Scorecard
          </h1>
          <p className="text-slate-400 mt-1">Your overall security posture at a glance</p>
        </div>

        {/* Main Score Card */}
        <div className={`rounded-2xl p-8 mb-8 border ${getScoreBg(scorecard?.overallScore || 0)}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm mb-2">Overall Security Score</p>
              <div className="flex items-baseline gap-4">
                <span className={`text-6xl font-bold ${getScoreColor(scorecard?.overallScore || 0)}`}>
                  {scorecard?.overallScore || 0}
                </span>
                <span className="text-slate-400 text-2xl">/ 100</span>
              </div>
              {trend && (
                <div className={`flex items-center gap-1 mt-2 ${trend.color}`}>
                  <trend.icon className="w-4 h-4" />
                  <span className="text-sm">{trend.text} from last period</span>
                </div>
              )}
            </div>
            <div className="text-right">
              <Shield className={`w-24 h-24 ${getScoreColor(scorecard?.overallScore || 0)} opacity-20`} />
            </div>
          </div>
        </div>

        {/* Component Scores */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle className="w-5 h-5 text-blue-400" />
              <span className="text-slate-300">Assessment Score</span>
            </div>
            <div className={`text-3xl font-bold ${getScoreColor(scorecard?.assessmentScore || 0)}`}>
              {scorecard?.assessmentScore || 0}%
            </div>
            <p className="text-slate-500 text-sm mt-2">
              {scorecard?.metrics?.completedAssessments || 0} of {scorecard?.metrics?.totalAssessments || 0} completed
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-5 h-5 text-orange-400" />
              <span className="text-slate-300">Vulnerability Score</span>
            </div>
            <div className={`text-3xl font-bold ${getScoreColor(scorecard?.vulnerabilityScore || 0)}`}>
              {scorecard?.vulnerabilityScore || 0}%
            </div>
            <p className="text-slate-500 text-sm mt-2">
              {scorecard?.metrics?.openVulnerabilities || 0} open ({scorecard?.metrics?.criticalVulnerabilities || 0} critical)
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-3 mb-4">
              <FileText className="w-5 h-5 text-purple-400" />
              <span className="text-slate-300">SSP Completion</span>
            </div>
            <div className={`text-3xl font-bold ${getScoreColor(scorecard?.sspScore || 0)}`}>
              {scorecard?.sspScore || 0}%
            </div>
            <p className="text-slate-500 text-sm mt-2">
              {scorecard?.metrics?.totalSSPs || 0} security plans
            </p>
          </div>
        </div>

        {/* Trend Chart */}
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-violet-400" />
            Score Trend (Last 30 Days)
          </h3>
          {scorecard?.trend && scorecard.trend.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={scorecard.trend}>
                <defs>
                  <linearGradient id="scoreGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" stroke="#64748b" tick={{ fontSize: 12 }} />
                <YAxis domain={[0, 100]} stroke="#64748b" tick={{ fontSize: 12 }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                  labelStyle={{ color: '#e2e8f0' }}
                />
                <Area
                  type="monotone"
                  dataKey="overallScore"
                  stroke="#8b5cf6"
                  strokeWidth={2}
                  fill="url(#scoreGradient)"
                  name="Overall Score"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-slate-500">
              <p>Not enough data for trend chart. Check back after a few days.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default SecurityScorecard;
